/**
 * YearManagement Component
 * 
 * Admin-only component for managing years.
 * Allows creating new years (with default Fachbereiche) and deleting years.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { useBackendStatus } from '../contexts/BackendStatusContext';
import './YearManagement.css';
import './Switch.css';
import './AdminHeader.css';

interface YearManagementProps {
  onClose: () => void;
  token: string;
}

const YearManagement: React.FC<YearManagementProps> = ({ onClose, token }) => {
  const { refreshUser, year: currentYear } = useAuth();
  const { pauseHealthCheck, resumeHealthCheck } = useBackendStatus();
  const [years, setYears] = useState<number[]>([]);
  const [yearMaintenanceStatus, setYearMaintenanceStatus] = useState<{ [year: number]: boolean }>({});
  const [yearRaceDayDates, setYearRaceDayDates] = useState<{ [year: number]: string | null }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [newYear, setNewYear] = useState<string>('');
  const [newRaceDayDate, setNewRaceDayDate] = useState<string>('');
  const [copyFromPreviousYear, setCopyFromPreviousYear] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [deleteProgress, setDeleteProgress] = useState<{ step: number; stepName: string } | null>(null);
  const [updatingMaintenance, setUpdatingMaintenance] = useState<number | null>(null);
  const [updatingRaceDayDate, setUpdatingRaceDayDate] = useState<number | null>(null);
  const [localRaceDayDates, setLocalRaceDayDates] = useState<{ [year: number]: string }>({});
  const [yearGoogleSheetsConfig, setYearGoogleSheetsConfig] = useState<{ [year: number]: { id: string | null; configured: boolean; writeConfigured?: boolean } }>({});
  const [expandedGoogleSheets, setExpandedGoogleSheets] = useState<{ [year: number]: boolean }>({});
  const [localGoogleSheetsConfig, setLocalGoogleSheetsConfig] = useState<{ [year: number]: { id: string; credentials: string; writeCredentials?: string; mapping: string; updateExisting?: boolean; googleFormUrl?: string } }>({});
  const [updatingGoogleSheets, setUpdatingGoogleSheets] = useState<number | null>(null);
  const [testingGoogleSheets, setTestingGoogleSheets] = useState<number | null>(null);
  const [cleaningGoogleSheets, setCleaningGoogleSheets] = useState<number | null>(null);

  // Handle Escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    fetchYears();
    fetchMaintenanceStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchYears = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/api/auth/available-years`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const fetchedYears = response.data.years || [];
        setYears(fetchedYears);
      } else {
        setError(response.data.message || 'Fehler beim Laden der Jahre');
      }
    } catch (err: any) {
      console.error('Error fetching years:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Jahre');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/years/maintenance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const statusMap: { [year: number]: boolean } = {};
        const raceDayDateMap: { [year: number]: string | null } = {};
        const googleSheetsMap: { [year: number]: { id: string | null; configured: boolean; writeConfigured?: boolean } } = {};
        response.data.years.forEach((item: { year: number; disabled: boolean; race_day_date?: string | null; google_sheets_id?: string | null; google_sheets_configured?: boolean }) => {
          statusMap[item.year] = item.disabled;
          raceDayDateMap[item.year] = item.race_day_date || null;
          googleSheetsMap[item.year] = {
            id: item.google_sheets_id || null,
            configured: item.google_sheets_configured || false
          };
        });
        setYearMaintenanceStatus(statusMap);
        setYearRaceDayDates(raceDayDateMap);
        setYearGoogleSheetsConfig(googleSheetsMap);
        // Initialize local state with fetched values
        const localMap: { [year: number]: string } = {};
        response.data.years.forEach((item: { year: number; race_day_date?: string | null }) => {
          if (item.race_day_date) {
            localMap[item.year] = item.race_day_date;
          }
        });
        setLocalRaceDayDates(localMap);
      }
    } catch (err: any) {
      console.error('Error fetching maintenance status:', err);
      // Don't show error, just use defaults (all enabled)
    }
  };

  const handleToggleMaintenance = async (year: number, disabled: boolean) => {
    // Prevent multiple simultaneous updates
    if (updatingMaintenance === year) {
      return;
    }
    
    try {
      setUpdatingMaintenance(year);
      setError('');
      // Get current race day date from state (use saved value, fallback to local)
      const savedRaceDayDate = yearRaceDayDates[year] || null;
      const localRaceDayDate = localRaceDayDates[year];
      const currentRaceDayDate = savedRaceDayDate || localRaceDayDate || null;

      const response = await axios.put(
        `${API_BASE_URL}/api/admin/years/${year}/maintenance`,
        { disabled, race_day_date: currentRaceDayDate },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Update maintenance status
        setYearMaintenanceStatus(prev => ({
          ...prev,
          [year]: disabled
        }));
        // Always update race_day_date from response (backend returns the saved value)
        const savedRaceDayDateFromResponse = response.data.race_day_date || null;
        setYearRaceDayDates(prev => ({
          ...prev,
          [year]: savedRaceDayDateFromResponse
        }));
        // Also update local state to keep it in sync
        if (savedRaceDayDateFromResponse) {
          setLocalRaceDayDates(prev => ({
            ...prev,
            [year]: savedRaceDayDateFromResponse
          }));
        } else {
          // Remove from local state if null
          setLocalRaceDayDates(prev => {
            const updated = { ...prev };
            delete updated[year];
            return updated;
          });
        }
      } else {
        const errorMsg = response.data.message || 'Fehler beim Aktualisieren der Einstellung';
        console.error(`[YearManagement] API returned error for year ${year}:`, errorMsg);
        setError(errorMsg);
        // Refresh maintenance status to get current state from server
        await fetchMaintenanceStatus();
      }
    } catch (err: any) {
      console.error(`[YearManagement] Error updating maintenance status for year ${year}:`, err);
      const errorMsg = err.response?.data?.message || err.message || 'Fehler beim Aktualisieren der Einstellung';
      setError(errorMsg);
      // Refresh maintenance status to get current state from server
      await fetchMaintenanceStatus();
    } finally {
      // Always clear updating state, even on error
      setUpdatingMaintenance(null);
    }
  };

  const handleUpdateRaceDayDate = async (year: number, raceDayDate: string) => {
    try {
      setUpdatingRaceDayDate(year);
      setError('');
      const disabled = yearMaintenanceStatus[year] || false;
      const requestBody = { disabled, race_day_date: raceDayDate || null };
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/years/${year}/maintenance`,
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data && response.data.success) {
        const savedDate = response.data.race_day_date || null;
        setYearRaceDayDates(prev => ({
          ...prev,
          [year]: savedDate
        }));
        // Update local state with saved value
        if (savedDate) {
          setLocalRaceDayDates(prev => ({
            ...prev,
            [year]: savedDate
          }));
        } else {
          setLocalRaceDayDates(prev => {
            const updated = { ...prev };
            delete updated[year];
            return updated;
          });
        }
      } else {
        setError(response.data.message || 'Fehler beim Aktualisieren des Wettkampftages');
        // Revert local state on error
        setLocalRaceDayDates(prev => {
          const updated = { ...prev };
          const originalDate = yearRaceDayDates[year];
          if (originalDate) {
            updated[year] = originalDate;
          } else {
            delete updated[year];
          }
          return updated;
        });
      }
    } catch (err: any) {
      console.error('Error updating race day date:', err);
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren des Wettkampftages');
      // Revert local state on error
      setLocalRaceDayDates(prev => {
        const updated = { ...prev };
        const originalDate = yearRaceDayDates[year];
        if (originalDate) {
          updated[year] = originalDate;
        } else {
          delete updated[year];
        }
        return updated;
      });
    } finally {
      setUpdatingRaceDayDate(null);
    }
  };

  const handleUpdateGoogleSheetsConfig = async (year: number) => {
    const localConfig = localGoogleSheetsConfig[year];
    if (!localConfig) {
      setError('Keine Änderungen zum Speichern');
      return;
    }

    try {
      setUpdatingGoogleSheets(year);
      setError('');

      // Check what fields have been changed (non-empty values)
      const hasId = localConfig.id.trim() !== '';
      const hasCredentials = localConfig.credentials.trim() !== '';
      const hasWriteCredentials = (localConfig.writeCredentials || '').trim() !== '';
      const hasMapping = localConfig.mapping.trim() !== '';
      
      // Validate JSON formats for fields that are provided
      let credentialsJson: string | undefined = undefined;
      let mappingJson: string | undefined = undefined;

      if (hasCredentials) {
        try {
          JSON.parse(localConfig.credentials);
          credentialsJson = localConfig.credentials.trim();
        } catch (e) {
          setError('Ungültiges Format für Credentials JSON');
          setUpdatingGoogleSheets(null);
          return;
        }
      }

      if (hasMapping) {
        try {
          const parsed = JSON.parse(localConfig.mapping) as Record<string, unknown>;
          // updateExisting is controlled only by the UI checkbox, not stored in the mapping JSON the user edits
          mappingJson = JSON.stringify({ ...parsed, updateExisting: localConfig.updateExisting !== false }, null, 2);
        } catch (e) {
          setError('Ungültiges Format für Field Mapping JSON');
          setUpdatingGoogleSheets(null);
          return;
        }
      }

      const sheetId = hasId ? localConfig.id.trim() : undefined;

      // Build request body - only include fields that are provided
      // Empty string means "keep existing", undefined means "not provided"
      const requestBody: any = {};
      if (hasId) {
        requestBody.google_sheets_id = sheetId;
      }
      if (hasCredentials) {
        requestBody.google_sheets_credentials_json = credentialsJson;
      }
      if (hasWriteCredentials) {
        try {
          JSON.parse(localConfig.writeCredentials!.trim());
          requestBody.google_sheets_write_credentials_json = localConfig.writeCredentials!.trim();
        } catch (e) {
          setError('Ungültiges Format für Write Credentials JSON');
          setUpdatingGoogleSheets(null);
          return;
        }
      }
      if (hasMapping) {
        requestBody.google_sheets_field_mapping_json = mappingJson;
      }

      // If all fields are empty, send null values to clear config
      const allEmpty = !hasId && !hasCredentials && !hasMapping;
      if (allEmpty) {
        requestBody.google_sheets_id = null;
        requestBody.google_sheets_credentials_json = null;
        requestBody.google_sheets_write_credentials_json = null;
        requestBody.google_sheets_field_mapping_json = null;
      }

      // Google Form URL is always sent when we have local config (independent of Sheets config)
      requestBody.google_form_url = (localConfig.googleFormUrl || '').trim() || null;

      const response = await axios.put(
        `${API_BASE_URL}/api/admin/years/${year}/google-sheets`,
        requestBody,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Refresh config state from server
        const configResponse = await axios.get(
          `${API_BASE_URL}/api/admin/years/${year}/google-sheets`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (configResponse.data.success) {
          const hasConfig = !!(configResponse.data.google_sheets_id && configResponse.data.google_sheets_field_mapping_json);
          setYearGoogleSheetsConfig(prev => ({
            ...prev,
            [year]: {
              ...prev[year],
              id: configResponse.data.google_sheets_id || null,
              configured: hasConfig,
              writeConfigured: !!configResponse.data.google_sheets_write_configured
            }
          }));
        }
        
        // Clear local editing state
        setLocalGoogleSheetsConfig(prev => {
          const updated = { ...prev };
          delete updated[year];
          return updated;
        });
        setExpandedGoogleSheets(prev => ({
          ...prev,
          [year]: false
        }));
      } else {
        setError(response.data.message || 'Fehler beim Aktualisieren der Google Sheets Konfiguration');
      }
    } catch (err: any) {
      console.error('Error updating Google Sheets config:', err);
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren der Google Sheets Konfiguration');
    } finally {
      setUpdatingGoogleSheets(null);
    }
  };

  const handleTestGoogleSheetsConnection = async (year: number) => {
    // Check if user's current year matches the year being tested
    if (currentYear !== year) {
      setError(`Zum Testen müssen Sie zuerst zum Jahr ${year} wechseln (aktuell: ${currentYear}). Verwenden Sie das Jahresauswahl-Menü (⚙️).`);
      return;
    }
    
    try {
      setTestingGoogleSheets(year);
      setError('');
      const response = await axios.get(
        `${API_BASE_URL}/api/google-sheets/test?year=${year}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 30000 // 30 second timeout
        }
      );

      if (response.data.success) {
        const sheetNames = response.data.sheets.map((s: any) => s.name);
        const sheetCount = sheetNames.length;
        const readStatus = response.data.readOk ? '✓ OK (Sync/Import möglich)' : '✗ Fehler';
        const writeStatus = response.data.writeConfigured
          ? (response.data.writeOk ? '✓ OK (Identitätsänderungen mit Rückschreibung möglich)' : `✗ Fehler: ${response.data.writeMessage || 'Unbekannt'}`)
          : '– nicht konfiguriert (nur Sync/Import, keine Rückschreibung)';
        const lines = [
          'Verbindung erfolgreich!',
          '',
          `Titel: ${response.data.sheetTitle}`,
          '',
          `Gefundene Tabellen (${sheetCount}):`,
          ...sheetNames.map((name: string, idx: number) => `  ${idx + 1}. ${name}`),
          '',
          'Read Credentials (Sync/Import):',
          `  ${readStatus}`,
          '',
          'Write Credentials (Identitätsänderungen):',
          `  ${writeStatus}`
        ];
        alert(lines.join('\n'));
      } else {
        setError(response.data.message || 'Verbindungstest fehlgeschlagen');
      }
    } catch (err: any) {
      console.error('Error testing Google Sheets connection:', err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Verbindungstest hat das Zeitlimit überschritten. Mögliche Ursachen: Netzwerkproblem oder Backend kann Google APIs nicht erreichen.');
      } else {
        setError(err.response?.data?.message || err.message || 'Fehler beim Testen der Verbindung');
      }
    } finally {
      setTestingGoogleSheets(null);
    }
  };

  const handleCleanupGoogleSheetsConfig = async (year: number) => {
    const confirmed = window.confirm(
      `Google Sheets Konfiguration für Jahr ${year} wirklich löschen?\n\n` +
      'Dies entfernt Sheet ID, Credentials, Field Mapping und Write Credentials für dieses Jahr. Die Helferdaten bleiben erhalten.'
    );
    if (!confirmed) return;

    try {
      setCleaningGoogleSheets(year);
      setError('');
      const response = await axios.delete(
        `${API_BASE_URL}/api/admin/years/${year}/google-sheets`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setYearGoogleSheetsConfig(prev => ({
          ...prev,
          [year]: {
            id: null,
            configured: false,
            writeConfigured: false
          }
        }));
        setExpandedGoogleSheets(prev => ({ ...prev, [year]: false }));
        setLocalGoogleSheetsConfig(prev => {
          const updated = { ...prev };
          delete updated[year];
          return updated;
        });
      } else {
        setError(response.data.message || 'Fehler beim Löschen der Google Sheets Konfiguration');
      }
    } catch (err: any) {
      console.error('Error cleaning up Google Sheets config:', err);
      setError(err.response?.data?.message || err.message || 'Fehler beim Löschen der Google Sheets Konfiguration');
    } finally {
      setCleaningGoogleSheets(null);
    }
  };

  const handleGoogleSheetsCredentialsFileUpload = (year: number, event: React.ChangeEvent<HTMLInputElement>, field: 'credentials' | 'writeCredentials') => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setLocalGoogleSheetsConfig(prev => ({
        ...prev,
        [year]: {
          ...prev[year],
          id: prev[year]?.id || '',
          credentials: field === 'credentials' ? content : (prev[year]?.credentials || ''),
          writeCredentials: field === 'writeCredentials' ? content : (prev[year]?.writeCredentials || ''),
          mapping: prev[year]?.mapping || '',
          updateExisting: prev[year]?.updateExisting !== false,
          googleFormUrl: prev[year]?.googleFormUrl || ''
        }
      }));
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset so same file can be selected again
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const yearNum = parseInt(newYear);
    if (isNaN(yearNum) || yearNum < 2020 || yearNum > 2100) {
      setError('Bitte geben Sie ein gültiges Jahr zwischen 2020 und 2100 ein');
      return;
    }

    // Note: We don't check locally anymore - let the backend handle it
    // This allows creating years even if the local list is stale

    try {
      setCreating(true);
      setError('');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/years`,
        { year: yearNum, race_day_date: newRaceDayDate || null, copy_from_previous_year: copyFromPreviousYear },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );


      if (response.data.success) {
        const createdYear = response.data.year;
        const createdRaceDayDate = response.data.race_day_date || null;
        
        // Update local state with the created year's race day date
        if (createdRaceDayDate) {
          setYearRaceDayDates(prev => ({
            ...prev,
            [createdYear]: createdRaceDayDate
          }));
          setLocalRaceDayDates(prev => ({
            ...prev,
            [createdYear]: createdRaceDayDate
          }));
        }
        
        setNewYear('');
        setNewRaceDayDate('');
        // Wait a bit for database to be ready
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchYears();
        await fetchMaintenanceStatus();
        // Refresh available years in auth context
        await refreshUser();
        alert(response.data.message);
      } else {
        const errorMsg = response.data.message || 'Fehler beim Erstellen des Jahres';
        setError(errorMsg);
        console.error('Year creation failed:', response.data);
        alert(`Fehler: ${errorMsg}`);
      }
    } catch (err: any) {
      console.error('Error creating year:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Fehler beim Erstellen des Jahres';
      setError(errorMsg);
      console.error('Full error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      // Always show alert for errors
      alert(`Fehler beim Erstellen des Jahres: ${errorMsg}`);
    } finally {
      setCreating(false);
    }
  };


  const handleDelete = async (year: number) => {
    const confirmed = window.confirm(
      `WARNUNG: Möchten Sie das Jahr ${year} wirklich löschen?\n\n` +
      `Dies wird ALLE Daten für dieses Jahr löschen:\n` +
      `- Alle Helfer\n` +
      `- Alle Fachbereiche\n` +
      `- Alle Helferpunkte\n` +
      `- Alle Zuordnungen\n` +
      `- Alle Benutzer-Rollen\n\n` +
      `Diese Aktion kann nicht rückgängig gemacht werden!`
    );

    if (!confirmed) {
      return;
    }

    const deletionSteps = [
      { step: 1, name: 'Lösche Helfer-Position-Zuordnungen...' },
      { step: 2, name: 'Lösche Helfer-Fachbereich-Zuordnungen...' },
      { step: 3, name: 'Lösche Benutzer-Fachbereich-Zuordnungen...' },
      { step: 4, name: 'Lösche Benutzer-Rollen...' },
      { step: 5, name: 'Lösche Helferpunkte...' },
      { step: 6, name: 'Lösche Helfer...' },
      { step: 7, name: 'Lösche Fachbereiche...' },
      { step: 8, name: 'Abschließen...' }
    ];

    let progressInterval: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let safetyTimeout: NodeJS.Timeout | null = null;
    const progressRef = { currentStep: 0, isComplete: false };

    const cleanup = () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
    };

    const finishDeletion = async (success: boolean, message?: string) => {
      progressRef.isComplete = true;
      cleanup();
      
      // Show completion message immediately (deletion is now fast)
      setDeleteProgress({ step: 8, stepName: success ? 'Abgeschlossen!' : 'Fehler aufgetreten' });
      
      // Clear progress quickly for fast deletions
      timeoutId = setTimeout(() => {
        setDeleteProgress(null);
        timeoutId = null;
      }, success ? 500 : 2000); // Faster clear on success (500ms), longer on error (2s)
    };

    try {
      setDeleting(year);
      pauseHealthCheck(); // Pause health checks during deletion to prevent false "backend unavailable" warnings
      setError('');
      setDeleteProgress({ step: 0, stepName: 'Starte Löschvorgang...' });

      // Safety timeout - ensure dialog closes after 60 seconds max (deletion is now optimized and fast)
      safetyTimeout = setTimeout(() => {
        console.warn('Deletion taking too long, forcing cleanup');
        finishDeletion(false);
      }, 60000); // 60 seconds safety timeout

      // Simulate progress updates - advance through steps quickly (deletion is now optimized)
      progressInterval = setInterval(() => {
        if (progressRef.isComplete) {
          cleanup();
          return;
        }
        
        progressRef.currentStep++;
        if (progressRef.currentStep <= deletionSteps.length) {
          if (progressRef.currentStep === deletionSteps.length) {
            // Final step
            setDeleteProgress({ step: 8, stepName: 'Abschließen...' });
          } else {
            const step = deletionSteps[progressRef.currentStep - 1];
            setDeleteProgress({ step: step.step, stepName: step.name });
          }
        }
      }, 150); // Update every 150ms (faster since deletion is now optimized)

      const response = await axios.delete(
        `${API_BASE_URL}/api/admin/years/${year}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 30000 // 30 second timeout (deletion is now optimized and fast, even for large datasets)
        }
      );

      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
      await finishDeletion(true);

      if (response.data.success) {
        await fetchYears();
        // Refresh available years in auth context
        await refreshUser();
        alert(response.data.message);
      } else {
        setError(response.data.message || 'Fehler beim Löschen des Jahres');
      }
    } catch (err: any) {
      console.error('Error deleting year:', err);
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
        safetyTimeout = null;
      }
      await finishDeletion(false);
      
      if (err.code === 'ECONNABORTED' || (err.message && err.message.includes('timeout'))) {
        setError('Die Löschung dauert länger als erwartet. Bitte prüfen Sie die Server-Logs oder versuchen Sie es später erneut.');
      } else {
        setError(err.response?.data?.message || err.message || 'Fehler beim Löschen des Jahres');
      }
    } finally {
      setDeleting(null);
      resumeHealthCheck(); // Resume health checks after deletion completes
    }
  };

  if (loading) {
    return (
      <div className="year-management-overlay">
        <div className="year-management-container">
          <div>Lädt...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="year-management-overlay">
      <div className="year-management-container">
        <div className="admin-header">
          <h2 className="admin-header-title">JAHRE VERWALTEN</h2>
          <button className="admin-header-close" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="year-management-error">{error}</div>
        )}

        <div className="year-management-content">
          <div className="year-management-section">
            <h3>Neues Jahr erstellen</h3>
            <form onSubmit={handleCreate} className="year-management-form">
              <div className="year-management-form-group">
                <label htmlFor="new-year">
                  Jahr:
                </label>
                <input
                  id="new-year"
                  type="number"
                  min="2020"
                  max="2100"
                  value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                  placeholder="z.B. 2026"
                  required
                  disabled={creating}
                  className="year-management-input"
                />
              </div>
              <div className="year-management-form-group">
                <label htmlFor="new-race-day-date">
                  Wettkampftag (optional):
                </label>
                <input
                  id="new-race-day-date"
                  type="date"
                  value={newRaceDayDate}
                  onChange={(e) => setNewRaceDayDate(e.target.value)}
                  disabled={creating}
                  className="year-management-input"
                />
                <small style={{ display: 'block', marginTop: '-4px', color: '#666', fontSize: '0.85rem' }}>Format: tt.mm.jjjj</small>
              </div>
              <div className="year-management-form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="copy-from-previous-year"
                  type="checkbox"
                  checked={copyFromPreviousYear}
                  onChange={(e) => setCopyFromPreviousYear(e.target.checked)}
                  disabled={creating}
                />
                <label htmlFor="copy-from-previous-year" style={{ margin: 0, cursor: 'pointer' }}>
                  Fachbereiche und Benutzerzuordnungen vom Vorjahr übernehmen
                </label>
              </div>
              <button
                type="submit"
                className="year-management-button year-management-button-primary"
                disabled={creating || !newYear}
              >
                {creating ? 'Erstelle...' : 'Jahr erstellen'}
              </button>
            </form>
          </div>

          <div className="year-management-section">
            <h3>Vorhandene Jahre</h3>
            {years.length === 0 ? (
              <p className="year-management-empty">Keine Jahre vorhanden</p>
            ) : (
              <div className="year-management-list">
                {years.map((year) => {
                  const maintenanceDisabled = yearMaintenanceStatus[year] || false;
                  const savedRaceDayDate = yearRaceDayDates[year] || null;
                  const localRaceDayDate = localRaceDayDates[year] !== undefined ? localRaceDayDates[year] : (savedRaceDayDate || '');
                  return (
                    <div key={year} className="year-management-item" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Main row: Year, Bearbeitung, Wettkampftag, Delete button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span className="year-management-year" style={{ marginRight: '24px' }}>{year}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '0.9rem', color: '#666', minWidth: '120px' }}>
                              Bearbeitung:
                            </span>
                            <label 
                              className={`switch-container ${updatingMaintenance === year ? 'disabled' : ''}`} 
                              style={{ 
                                opacity: updatingMaintenance === year ? 0.6 : 1, 
                                cursor: updatingMaintenance === year ? 'not-allowed' : 'pointer' 
                              }} 
                              title={updatingMaintenance === year ? 'Wird aktualisiert...' : maintenanceDisabled ? 'Bearbeitung aktivieren' : 'Bearbeitung deaktivieren (Jahr wird schreibgeschützt)'}
                            >
                              <input
                                type="checkbox"
                                className="switch-input"
                                checked={!maintenanceDisabled}
                                onChange={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (updatingMaintenance !== year) {
                                    const newDisabledValue = !maintenanceDisabled;
                                    handleToggleMaintenance(year, newDisabledValue);
                                  }
                                }}
                                disabled={updatingMaintenance === year}
                                readOnly={updatingMaintenance === year}
                              />
                              <span className="switch-track switch-track-red">
                                <span className="switch-thumb" />
                              </span>
                            </label>
                            <span style={{ fontSize: '0.85rem', color: '#666', minWidth: '60px' }}>
                              {maintenanceDisabled ? 'Deaktiviert' : 'Aktiviert'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '0.9rem', color: '#666', minWidth: '100px' }}>
                              Wettkampftag:
                            </span>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <input
                                type="date"
                                value={localRaceDayDate || ''}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setLocalRaceDayDates(prev => ({
                                    ...prev,
                                    [year]: newValue
                                  }));
                                }}
                                onBlur={(e) => {
                                  const newValue = e.target.value;
                                  if (newValue !== (savedRaceDayDate || '')) {
                                    handleUpdateRaceDayDate(year, newValue);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const newValue = e.currentTarget.value;
                                    if (newValue !== (savedRaceDayDate || '')) {
                                      handleUpdateRaceDayDate(year, newValue);
                                    }
                                    e.currentTarget.blur();
                                  }
                                }}
                                disabled={updatingRaceDayDate === year}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '0.9rem',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  opacity: updatingRaceDayDate === year ? 0.6 : 1,
                                  cursor: updatingRaceDayDate === year ? 'not-allowed' : 'pointer',
                                  backgroundColor: '#f0f0f0'
                                }}
                              />
                              <small style={{ display: 'block', marginTop: '-4px', color: '#666', fontSize: '0.85rem' }}>Format: tt.mm.jjjj</small>
                            </div>
                          </div>
                        </div>
                        <button
                          className="year-management-button year-management-button-danger"
                          onClick={() => handleDelete(year)}
                          disabled={deleting === year}
                        >
                          {deleting === year ? 'Lösche...' : 'Löschen'}
                        </button>
                      </div>
                      
                      {/* Google Sheets section - below the main row */}
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '4px',
                        border: '1px solid #dee2e6',
                        marginLeft: '0'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: '500', minWidth: '120px' }}>
                            Google Sheets:
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <span style={{ fontSize: '0.85rem', color: yearGoogleSheetsConfig[year]?.configured ? '#28a745' : '#666' }}>
                              {yearGoogleSheetsConfig[year]?.configured ? '✓ Konfiguriert' : 'Nicht konfiguriert'}
                            </span>
                            {yearGoogleSheetsConfig[year]?.configured && (
                                <button
                                  onClick={() => handleTestGoogleSheetsConnection(year)}
                                  disabled={testingGoogleSheets === year || currentYear !== year}
                                  title={currentYear !== year ? `Zum Testen müssen Sie zuerst zum Jahr ${year} wechseln (aktuell: ${currentYear})` : 'Google Sheets Verbindung testen'}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '0.8rem',
                                    backgroundColor: currentYear === year ? '#007bff' : '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: (testingGoogleSheets === year || currentYear !== year) ? 'not-allowed' : 'pointer',
                                    opacity: (testingGoogleSheets === year || currentYear !== year) ? 0.6 : 1
                                  }}
                                >
                                  {testingGoogleSheets === year ? 'Teste...' : currentYear !== year ? 'Test (Jahr wechseln)' : 'Test'}
                                </button>
                            )}
                            <button
                              onClick={async () => {
                                const isExpanded = expandedGoogleSheets[year];
                                setExpandedGoogleSheets(prev => ({
                                  ...prev,
                                  [year]: !isExpanded
                                }));
                                // Initialize local config when expanding
                                if (!isExpanded && !localGoogleSheetsConfig[year]) {
                                  try {
                                    // Fetch full config (without credentials for security)
                                    const response = await axios.get(
                                      `${API_BASE_URL}/api/admin/years/${year}/google-sheets`,
                                      {
                                        headers: {
                                          'Authorization': `Bearer ${token}`
                                        }
                                      }
                                    );
                                    if (response.data.success) {
                                      const mappingStr = response.data.google_sheets_field_mapping_json || '';
                                      let updateExisting = true;
                                      let mappingForDisplay = '{}';
                                      try {
                                        const parsed = mappingStr ? JSON.parse(mappingStr) : {};
                                        updateExisting = parsed.updateExisting !== false;
                                        const { updateExisting: _ue, ...mappingWithout } = parsed;
                                        mappingForDisplay = JSON.stringify(mappingWithout, null, 2);
                                      } catch (_) {
                                        mappingForDisplay = mappingStr || '{}';
                                      }
                                      setYearGoogleSheetsConfig(prev => ({
                                        ...prev,
                                        [year]: {
                                          ...prev[year],
                                          id: response.data.google_sheets_id || null,
                                          configured: !!(response.data.google_sheets_id && mappingStr),
                                          writeConfigured: !!response.data.google_sheets_write_configured
                                        }
                                      }));
                                      setLocalGoogleSheetsConfig(prev => ({
                                        ...prev,
                                        [year]: {
                                          id: response.data.google_sheets_id || '',
                                          credentials: '', // Not returned for security
                                          writeCredentials: '', // Not returned for security
                                          mapping: mappingForDisplay,
                                          updateExisting,
                                          googleFormUrl: response.data.google_form_url || ''
                                        }
                                      }));
                                    } else {
                                      setLocalGoogleSheetsConfig(prev => ({
                                        ...prev,
                                        [year]: { id: '', credentials: '', writeCredentials: '', mapping: '', updateExisting: true, googleFormUrl: '' }
                                      }));
                                    }
                                  } catch (err) {
                                    setLocalGoogleSheetsConfig(prev => ({
                                      ...prev,
                                      [year]: { id: '', credentials: '', writeCredentials: '', mapping: '', updateExisting: true, googleFormUrl: '' }
                                    }));
                                  }
                                }
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: '0.8rem',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              {expandedGoogleSheets[year] ? 'Ausblenden' : 'Konfigurieren'}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded configuration form - appears below Google Sheets section */}
                      {expandedGoogleSheets[year] && (
                        <div style={{ 
                          marginTop: '16px', 
                          padding: '16px', 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: '4px',
                          border: '1px solid #dee2e6'
                        }}>
                          <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', fontWeight: '600' }}>Google Sheets Konfiguration</h4>
                          {yearGoogleSheetsConfig[year]?.configured && (
                            <div style={{ marginBottom: '12px' }}>
                              <button
                                type="button"
                                onClick={() => handleCleanupGoogleSheetsConfig(year)}
                                disabled={cleaningGoogleSheets === year}
                                title="Google Sheets Konfiguration für dieses Jahr löschen (Sheet ID, Credentials, Zuordnung). Helferdaten bleiben erhalten."
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '0.85rem',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: cleaningGoogleSheets === year ? 'not-allowed' : 'pointer',
                                  opacity: cleaningGoogleSheets === year ? 0.65 : 1
                                }}
                              >
                                {cleaningGoogleSheets === year ? 'Lösche Konfiguration…' : 'Konfiguration löschen'}
                              </button>
                            </div>
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: '500' }}>
                                Google Sheet ID:
                              </label>
                              <input
                                type="text"
                                value={localGoogleSheetsConfig[year]?.id || ''}
                                onChange={(e) => {
                                  setLocalGoogleSheetsConfig(prev => ({
                                    ...prev,
                                    [year]: {
                                      ...prev[year],
                                      id: e.target.value,
                                      credentials: prev[year]?.credentials || '',
                                      mapping: prev[year]?.mapping || ''
                                    }
                                  }));
                                }}
                                placeholder="1v5Oy8vuapjwh2pMub7-k_AMKQ9Ac1ReSDT0GyV10vYU"
                                style={{
                                  width: '100%',
                                  padding: '6px 8px',
                                  fontSize: '0.85rem',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px'
                                }}
                              />
                              <small style={{ display: 'block', marginTop: '2px', color: '#666', fontSize: '0.75rem' }}>
                                Aus der Google Sheet URL extrahieren
                              </small>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: '500' }}>
                                Credentials JSON:
                              </label>
                              <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                                <input
                                  type="file"
                                  accept=".json"
                                  onChange={(e) => handleGoogleSheetsCredentialsFileUpload(year, e, 'credentials')}
                                  style={{ fontSize: '0.8rem' }}
                                />
                                <span style={{ fontSize: '0.75rem', color: '#666', alignSelf: 'center' }}>oder</span>
                              </div>
                              <textarea
                                value={localGoogleSheetsConfig[year]?.credentials || ''}
                                onChange={(e) => {
                                  setLocalGoogleSheetsConfig(prev => ({
                                    ...prev,
                                    [year]: {
                                      ...prev[year],
                                      id: prev[year]?.id || '',
                                      credentials: e.target.value,
                                      writeCredentials: prev[year]?.writeCredentials || '',
                                      mapping: prev[year]?.mapping || ''
                                    }
                                  }));
                                }}
                                placeholder={yearGoogleSheetsConfig[year]?.configured
                                  ? 'Leer lassen, um vorhandene Credentials beizubehalten'
                                  : '{"type": "service_account", "project_id": "...", ...}'}
                                rows={6}
                                style={{
                                  width: '100%',
                                  padding: '6px 8px',
                                  fontSize: '0.8rem',
                                  fontFamily: 'monospace',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  resize: 'vertical'
                                }}
                              />
                              <small style={{ display: 'block', marginTop: '2px', color: '#666', fontSize: '0.75rem' }}>
                                Service Account mit Viewer-Berechtigung. Ermöglicht Sync und Import von Helfern aus der Google-Tabelle.
                              </small>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: '500' }}>
                                Write Credentials JSON (optional):
                              </label>
                              <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                                <input
                                  type="file"
                                  accept=".json"
                                  onChange={(e) => handleGoogleSheetsCredentialsFileUpload(year, e, 'writeCredentials')}
                                  style={{ fontSize: '0.8rem' }}
                                />
                                <span style={{ fontSize: '0.75rem', color: '#666', alignSelf: 'center' }}>oder</span>
                              </div>
                              <textarea
                                value={localGoogleSheetsConfig[year]?.writeCredentials || ''}
                                onChange={(e) => {
                                  setLocalGoogleSheetsConfig(prev => ({
                                    ...prev,
                                    [year]: {
                                      ...prev[year],
                                      id: prev[year]?.id || '',
                                      credentials: prev[year]?.credentials || '',
                                      writeCredentials: e.target.value,
                                      mapping: prev[year]?.mapping || ''
                                    }
                                  }));
                                }}
                                placeholder={yearGoogleSheetsConfig[year]?.writeConfigured
                                  ? 'Leer lassen, um vorhandene Write Credentials beizubehalten'
                                  : 'Für Identitätsänderungen mit Rückschreibung in die Tabelle. Separater Service Account mit Editor-Berechtigung.'}
                                rows={4}
                                style={{
                                  width: '100%',
                                  padding: '6px 8px',
                                  fontSize: '0.8rem',
                                  fontFamily: 'monospace',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  resize: 'vertical'
                                }}
                              />
                              <small style={{ display: 'block', marginTop: '2px', color: '#666', fontSize: '0.75rem' }}>
                                Service Account mit Editor-Berechtigung. Ermöglicht Rückschreibung von Identitätsänderungen (Vorname, Nachname, E-Mail) in die Google-Tabelle.
                              </small>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: '500' }}>
                                Field Mapping JSON:
                              </label>
                              <textarea
                                value={localGoogleSheetsConfig[year]?.mapping || ''}
                                onChange={(e) => {
                                  setLocalGoogleSheetsConfig(prev => ({
                                    ...prev,
                                    [year]: {
                                      ...prev[year],
                                      id: prev[year]?.id || '',
                                      credentials: prev[year]?.credentials || '',
                                      mapping: e.target.value,
                                      updateExisting: prev[year]?.updateExisting !== false
                                    }
                                  }));
                                }}
                                placeholder='{"firstName": ["Vorname", "vorname"], "surname": ["Nachname", "nachname"], ...}'
                                rows={8}
                                style={{
                                  width: '100%',
                                  padding: '6px 8px',
                                  fontSize: '0.8rem',
                                  fontFamily: 'monospace',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  resize: 'vertical'
                                }}
                              />
                              <small style={{ display: 'block', marginTop: '2px', color: '#666', fontSize: '0.75rem' }}>
                                Definiert, wie Google Sheet Spalten den Helfer-Feldern zugeordnet werden. Optionale flagRules und fachbereichRules (siehe GOOGLE_SHEETS_SETUP.md).
                              </small>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', fontWeight: '500' }}>
                                Google Form URL (für Helfer-Registrierung):
                              </label>
                              <input
                                type="url"
                                value={localGoogleSheetsConfig[year]?.googleFormUrl || ''}
                                onChange={(e) => {
                                  setLocalGoogleSheetsConfig(prev => ({
                                    ...prev,
                                    [year]: {
                                      ...prev[year],
                                      id: prev[year]?.id || '',
                                      credentials: prev[year]?.credentials || '',
                                      mapping: prev[year]?.mapping || '',
                                      updateExisting: prev[year]?.updateExisting !== false,
                                      googleFormUrl: e.target.value
                                    }
                                  }));
                                }}
                                placeholder="https://docs.google.com/forms/d/e/.../viewform"
                                style={{
                                  width: '100%',
                                  padding: '6px 8px',
                                  fontSize: '0.85rem',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px'
                                }}
                              />
                              <small style={{ display: 'block', marginTop: '2px', color: '#666', fontSize: '0.75rem' }}>
                                URL zum Google Formular für die Helfer-Registrierung. Wird Nutzern (Fachbereich Manager) als Option &quot;Helfer registrieren&quot; angezeigt.
                              </small>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                                <input
                                  type="checkbox"
                                  checked={localGoogleSheetsConfig[year]?.updateExisting !== false}
                                  onChange={(e) => {
                                    const newValue = e.target.checked;
                                    if (newValue && localGoogleSheetsConfig[year]?.updateExisting === false) {
                                      const confirmed = window.confirm(
                                        'Hinweis: "Vorhandene Helfer aktualisieren" aktivieren?\n\n' +
                                        'Wenn aktiviert, werden bei der Synchronisation bestehende Helfer (gleiche E-Mail) mit den Daten aus dem Google Sheet aktualisiert – inkl. Fachbereichs-Zuordnungen, Wunscheinsatzort, Einsatzzeit usw.\n\n' +
                                        'Fachbereich-Zuordnungen und fachbereichRules im Field Mapping werden dabei nicht entfernt, sondern erweitert.'
                                      );
                                      if (!confirmed) return;
                                    }
                                    setLocalGoogleSheetsConfig(prev => ({
                                      ...prev,
                                      [year]: {
                                        ...prev[year],
                                        id: prev[year]?.id || '',
                                        credentials: prev[year]?.credentials || '',
                                        mapping: prev[year]?.mapping || '',
                                        updateExisting: newValue,
                                        googleFormUrl: prev[year]?.googleFormUrl || ''
                                      }
                                    }));
                                  }}
                                />
                                Vorhandene Helfer aktualisieren
                              </label>
                              <small style={{ display: 'block', marginTop: '2px', color: '#666', fontSize: '0.75rem' }}>
                                Wenn deaktiviert: Nur neue Helfer werden importiert; bestehende Einträge werden übersprungen.
                              </small>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => {
                                  setExpandedGoogleSheets(prev => ({
                                    ...prev,
                                    [year]: false
                                  }));
                                  setLocalGoogleSheetsConfig(prev => {
                                    const updated = { ...prev };
                                    delete updated[year];
                                    return updated;
                                  });
                                }}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '0.85rem',
                                  backgroundColor: '#6c757d',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                Abbrechen
                              </button>
                              <button
                                onClick={() => handleUpdateGoogleSheetsConfig(year)}
                                disabled={updatingGoogleSheets === year}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '0.85rem',
                                  backgroundColor: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: updatingGoogleSheets === year ? 'not-allowed' : 'pointer',
                                  opacity: updatingGoogleSheets === year ? 0.6 : 1
                                }}
                              >
                                {updatingGoogleSheets === year ? 'Speichere...' : 'Speichern'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Progress Dialog */}
      {deleting !== null && deleteProgress && (
        <div className="year-management-progress-overlay">
          <div className="year-management-progress-dialog">
            <h3>Jahr {deleting} wird gelöscht...</h3>
            <div className="year-management-progress-content">
              <div className="year-management-progress-step">
                {deleteProgress.stepName}
              </div>
              <div className="year-management-progress-bar">
                <div 
                  className="year-management-progress-bar-fill"
                  style={{ width: `${(deleteProgress.step / 8) * 100}%` }}
                />
              </div>
              <div className="year-management-progress-text">
                Schritt {deleteProgress.step} von 8
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YearManagement;

